import { mdiContentCopy } from '@mdi/js';
import Icon from '@mdi/react';
import { Box, Button, IconButton, TextField, Typography } from '@mui/material';
import { Loading } from '@vabembarqueja/embarque-ja-shared-components';
import { useState } from 'react';

import { useUserContext } from 'src/contexts/User.context';
import { BoletoResponse } from 'src/models/PagamentoBoleto.Model';
import PagamentoService from 'src/services/Pagamento.service';
import { Colors } from 'src/shared/colors';
import { useAppSelector } from 'src/shared/hooks/redux';
import { paymentSelector } from 'src/shared/store/Pagamento/payment.selectors';

interface Props {
  onPaymentFailed: (reason?: string) => void;
  pagamentoValor?: number;
  pagamentoCpfCnpj?: string;
  pagamentoId?: string;
}

const PagamentoPorBoleto = ({
  onPaymentFailed,
  pagamentoValor,
  pagamentoCpfCnpj,
  pagamentoId
}: Props) => {
  const [showBoletoDetails, setShowBoletoDetails] = useState(false);
  const [boletoNumber, setBoletoNumber] = useState('');
  const [htlmUrl, setHtlmUrl] = useState('');
  const payment = useAppSelector(paymentSelector);
  const { user } = useUserContext();

  const [errorMsg, setErrorMsg] = useState('');
  const today = new Date();
  const [loading, setLoading] = useState(false);
  const [expirationDate, setExpirationDate] = useState('');
  const valor = payment.valor && payment.valor > 0 ? payment.valor : pagamentoValor;

  const copiar = () => {
    navigator.clipboard.writeText(boletoNumber);
  };
  const handleProsseguirClick = async () => {
    setLoading(true);
    setErrorMsg('');
    if (valor == null || valor <= 0) {
      setErrorMsg('Valor do pagamento inválido.');
      return;
    }
    try {
      const boleto: BoletoResponse = await PagamentoService.criarBoleto({
        valor,
        vencimento: today.toISOString().split('T')[0],
        cpfCnpj: pagamentoCpfCnpj || '',
        pagamentoId
      });

      setBoletoNumber(boleto.typefulLine.replace(/\D/g, ''));
      setExpirationDate(boleto.expirationDate);

      setHtlmUrl(boleto.links.find((l) => l.rel === 'boleto_html')?.href || '');

      setShowBoletoDetails(true);
    } catch (err: any) {
      setErrorMsg(err?.message || 'Erro ao gerar boleto');
      console.error('Erro ao gerar boleto', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBaixarPdf = () => {
    if (htlmUrl) {
      window.open(htlmUrl, '_blank');
    }
  };

  if (errorMsg) onPaymentFailed();
  if (loading) return <Loading show={loading} bgColor={Colors.white} />;
  return (
    <Box flex={1} display="flex" flexDirection="column" sx={{ paddingLeft: 2 }}>
      <Typography variant="h6" color="secondary.main">
        Boleto Bancário
      </Typography>
      {/* Primeira tela - Informações do boleto */}
      {!showBoletoDetails && (
        <Box display="flex" flexDirection="column" alignItems="flex-start" mt={2}>
          <Typography variant="body1" color={Colors.error.red} sx={{ marginBottom: 1 }}>
            Atenção
          </Typography>

          <Typography variant="body2" color="text.secondary" align="left" sx={{ marginBottom: 1 }}>
            * O pedido somente será processado após a confirmação do pagamento do boleto.
          </Typography>

          <Typography variant="body2" color="text.secondary" align="left" sx={{ marginBottom: 1 }}>
            * Não serão aceitos pagamentos diferentes do valor do boleto ou após a data de
            vencimento.
          </Typography>

          <Typography variant="body2" color="text.secondary" align="left" sx={{ marginBottom: 1 }}>
            * Não pague o boleto com cheque e não realize depósito, transferência bancária ou pix.
          </Typography>

          <Typography variant="body2" color="text.secondary" align="left" sx={{ marginBottom: 1 }}>
            * O não pagamento do boleto não incorrerá em nenhuma multa ou juros e seu pedido será
            automaticamente cancelado.
          </Typography>

          <Box display="flex" alignContent="flex-end" mt={2} width={1}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleProsseguirClick}
              disabled={loading}
            >
              <Typography textTransform="none">Prosseguir</Typography>
            </Button>
          </Box>
        </Box>
      )}

      {/* Segunda tela - Detalhes do boleto */}
      {showBoletoDetails && (
        <Box display="flex" flexDirection="column" alignItems="flex-start" mt={1}>
          <Typography variant="body1" color="text.primary" sx={{ marginBottom: 1 }}>
            O boleto no valor de <strong>R$ {Number(valor).toFixed(2).replace('.', ',')}</strong>,
            com vencimento em <strong>{expirationDate}</strong>, foi gerado com sucesso.
            {user?.email ? (
              <>
                {' '}
                O boleto foi enviado para o e-mail: <strong>{user?.email}</strong>.
              </>
            ) : (
              <> O boleto foi enviado para o e-mail cadastrado no sistema.</>
            )}
          </Typography>

          <Typography variant="body1" color="text.primary" sx={{ marginBottom: 2 }}>
            Utilize os botões abaixo para <strong>abrir o PDF do boleto</strong> ou{' '}
            <strong>copiar o código de barras</strong>.
          </Typography>

          <Box display="flex" alignItems="center" mt={1} mb={2} sx={{ width: '100%' }}>
            <TextField
              label="Número do Boleto"
              value={boletoNumber}
              variant="outlined"
              fullWidth
              disabled
            />
            <IconButton onClick={copiar} sx={{ marginLeft: 1 }}>
              <Icon path={mdiContentCopy} size={1} color={Colors.primary.main} />
            </IconButton>
          </Box>

          <Button fullWidth variant="contained" onClick={handleBaixarPdf}>
            <Typography textTransform="none">Exibir/Baixar boleto</Typography>
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default PagamentoPorBoleto;
